import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, PaperPlaneTilt, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtNumber} from './_lib';

const Bar = ({pct, color='#10b981'}) => (
	<div className="h-3 w-full rounded-full bg-slate-100 overflow-hidden">
		<div style={{width: `${Math.min(100, pct)}%`, backgroundColor: color}} className="h-full transition-all"/>
	</div>
);

export default function SendingStatus() {
	const navigate = useNavigate();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const res = await axios.get(`${NEUGENCE_API_BASE}/api/v1/sending-status`, {headers});
			setData(res.data || {});
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, []);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);
	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	const local = (data && data.last_24h_local) || {};
	const sentPct = data && data.max_24_hour_send ? (data.sent_last_24_hours / data.max_24_hour_send * 100) : 0;
	const bouncePct = local.bounce_rate || 0;
	const complaintPct = local.complaint_rate || 0;

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-4xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="mb-8">
					<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
						<PaperPlaneTilt size={28} weight="duotone" color="#3b82f6"/> Sending status
					</h1>
					<p className="text-sm text-slate-500 mt-1">SES quota + reputation. Bounce rate and complaint rate must stay low or AWS throttles.</p>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-20">Loading…</div>}

				{!loading && data && (
					<div className="space-y-6">
						<div className="rounded-2xl border border-slate-200 bg-white p-6">
							<h2 className="text-sm font-semibold text-slate-900 mb-4">SES account</h2>
							<div className="grid grid-cols-2 gap-4 text-sm">
								<div><span className="text-slate-500">Production access</span><div className="font-medium">{data.production_access===true?'✓ enabled':data.production_access===false?'⚠ sandbox':'—'}</div></div>
								<div><span className="text-slate-500">Sending</span><div className="font-medium">{data.sending_enabled===true?'✓ enabled':'⚠ paused'}</div></div>
								<div><span className="text-slate-500">Region</span><div className="font-medium">us-east-2</div></div>
								<div><span className="text-slate-500">Send rate limit</span><div className="font-medium">{data.max_send_rate || '—'} / sec</div></div>
							</div>
						</div>

						<div className="rounded-2xl border border-slate-200 bg-white p-6">
							<div className="flex items-center justify-between mb-2">
								<h2 className="text-sm font-semibold text-slate-900">24-hour quota usage</h2>
								<div className="text-xs text-slate-500">{fmtNumber(data.sent_last_24_hours||0)} / {fmtNumber(data.max_24_hour_send||0)}</div>
							</div>
							<Bar pct={sentPct} color={sentPct>80?'#ef4444':sentPct>50?'#f59e0b':'#10b981'}/>
							<div className="text-xs text-slate-500 mt-2">{sentPct.toFixed(2)}% of daily cap used.</div>
						</div>

						<div className="rounded-2xl border border-slate-200 bg-white p-6">
							<h2 className="text-sm font-semibold text-slate-900 mb-4">Last-24h locally observed</h2>
							<div className="grid grid-cols-2 gap-4 text-sm mb-4">
								<div><span className="text-slate-500">Sends</span><div className="font-medium text-2xl">{fmtNumber(local.send||0)}</div></div>
								<div><span className="text-slate-500">Deliveries</span><div className="font-medium text-2xl">{fmtNumber(local.delivery||0)}</div></div>
								<div><span className="text-slate-500">Bounces</span><div className="font-medium text-2xl">{fmtNumber(local.bounce||0)}</div></div>
								<div><span className="text-slate-500">Complaints</span><div className="font-medium text-2xl">{fmtNumber(local.complaint||0)}</div></div>
							</div>
							<div className="text-xs text-slate-500 mb-1">Bounce rate (must stay &lt; 5%)</div>
							<Bar pct={bouncePct*20} color={bouncePct>5?'#ef4444':bouncePct>2?'#f59e0b':'#10b981'}/>
							<div className="text-xs text-slate-500 mt-1 mb-4">{bouncePct.toFixed(2)}%</div>
							<div className="text-xs text-slate-500 mb-1">Complaint rate (must stay &lt; 0.1%)</div>
							<Bar pct={complaintPct*1000} color={complaintPct>0.1?'#ef4444':complaintPct>0.05?'#f59e0b':'#10b981'}/>
							<div className="text-xs text-slate-500 mt-1">{complaintPct.toFixed(3)}%</div>
						</div>

						{data._aws_error && (
							<div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
								<strong>SES API not reachable:</strong> {data._aws_error}
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
}
